import React, { useState, useEffect, useRef } from 'react';
import { Combobox } from '@headlessui/react';
import { Plus, Check, X, ChevronDown } from 'lucide-react';
import { optionsAPI } from '../../services/api';

/**
 * Dropdown that lets staff add new options on the fly.
 * Options are persisted to the backend under `optionsKey`.
 *
 * Props:
 *   optionsKey    — backend key (e.g. "stbModels", "entityCodes")
 *   value         — current selected value (string)
 *   onChange      — (value: string) => void
 *   placeholder   — shown as first disabled option
 *   themeColor    — optional accent color
 *   extraOptions  — additional values to merge in (e.g. every distinct value already used
 *                   across real customer records) alongside the admin-curated optionsKey list,
 *                   so the dropdown isn't limited to whatever's been manually added via "+".
 *   searchable    — type-to-filter combobox (headlessui) instead of a plain native <select>,
 *                   for long option lists (e.g. STB Model, Entity Code) where scrolling a
 *                   native dropdown is slow.
 */
export default function AddableSelect({ optionsKey, value, onChange, placeholder = 'Select…', themeColor, extraOptions, searchable = false }) {
  const [savedOptions, setSavedOptions] = useState([]);
  const [adding, setAdding]   = useState(false);
  const [newVal, setNewVal]   = useState('');
  const [saving, setSaving]   = useState(false);
  const [query, setQuery]     = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    optionsAPI.get(optionsKey)
      .then(({ data }) => setSavedOptions(data.data || []))
      .catch(() => {});
  }, [optionsKey]);

  const options = Array.from(new Set([...(extraOptions || []), ...savedOptions])).sort();
  const filteredOptions = query === '' ? options : options.filter(o => o.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const saveNew = async () => {
    const trimmed = newVal.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const { data } = await optionsAPI.add(optionsKey, trimmed);
      setSavedOptions(data.data || []);
      onChange(trimmed);
      setNewVal('');
      setAdding(false);
    } catch {
      // keep dialog open
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveNew(); }
    if (e.key === 'Escape') { setAdding(false); setNewVal(''); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        {searchable ? (
          <Combobox value={value} onChange={onChange}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Combobox.Input
                className="form-control"
                style={{ width: '100%', paddingRight: 30, boxSizing: 'border-box' }}
                displayValue={v => v || ''}
                placeholder={placeholder}
                onChange={e => setQuery(e.target.value)}
              />
              <Combobox.Button style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer' }}>
                <ChevronDown size={14} color="var(--c-6b7280)" />
              </Combobox.Button>
              <Combobox.Options style={{
                position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4,
                maxHeight: 240, overflowY: 'auto', background: 'var(--cb-ffffff)', border: '1px solid var(--cb-d1d5db)',
                borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: 4, listStyle: 'none',
              }}>
                {filteredOptions.length === 0 && (
                  <div style={{ padding: '8px 10px', fontSize: '.85rem', color: 'var(--c-9ca3af)' }}>No matches</div>
                )}
                {filteredOptions.map(opt => (
                  <Combobox.Option key={opt} value={opt} style={{ listStyle: 'none' }}>
                    {({ active, selected: isSelected }) => (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '7px 10px', borderRadius: 4, fontSize: '.88rem', cursor: 'pointer',
                        background: active ? 'var(--cb-eff6ff)' : 'transparent', color: 'var(--c-111111)',
                      }}>
                        <span>{opt}</span>
                        {isSelected && <Check size={14} color={themeColor || 'var(--c-1e3a8a)'} />}
                      </div>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </div>
          </Combobox>
        ) : (
          <select
            className="form-control"
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">{placeholder}</option>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          title="Add new option"
          onClick={() => { setAdding(a => !a); setNewVal(''); }}
          style={{
            flexShrink: 0,
            width: 36, height: 36,
            borderRadius: 8,
            border: `1.5px solid ${adding ? themeColor || 'var(--cb-1e3a8a)' : 'var(--cb-e5e7eb)'}`,
            background: adding ? (themeColor ? `${themeColor}15` : 'var(--cb-dbeafe)') : 'var(--bg)',
            color: adding ? (themeColor || 'var(--c-1e3a8a)') : 'var(--c-6b7280)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
        >
          <Plus size={16} strokeWidth={2} />
        </button>
      </div>

      {adding && (
        <div style={{
          display: 'flex', gap: 6, marginTop: 8,
          padding: '8px 10px',
          background: themeColor ? `${themeColor}08` : 'var(--cb-eff6ff)',
          borderRadius: 8,
          border: `1px dashed ${themeColor || '#1e3a8a'}50`,
        }}>
          <input
            ref={inputRef}
            value={newVal}
            onChange={e => setNewVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type new option and press Enter"
            style={{
              flex: 1, border: `1px solid ${themeColor || '#1e3a8a'}60`,
              borderRadius: 6, padding: '4px 8px', fontSize: '0.85rem',
              outline: 'none', background: 'white',
            }}
          />
          <button
            type="button"
            onClick={saveNew}
            disabled={saving || !newVal.trim()}
            style={{
              padding: '4px 10px', borderRadius: 6, border: 'none',
              background: themeColor || 'var(--cb-1e3a8a)', color: 'var(--c-ffffff)',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
              opacity: !newVal.trim() ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Check size={13} /> {saving ? 'Saving…' : 'Add'}
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setNewVal(''); }}
            style={{
              padding: '4px 8px', borderRadius: 6,
              border: '1px solid var(--cb-e5e7eb)', background: 'var(--bg)',
              cursor: 'pointer', color: 'var(--c-6b7280)',
              display: 'flex', alignItems: 'center',
            }}
          >
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
